/**
 * Permission Group Redux Slice
 * Phase 5: RBAC & Permission System - M4
 * 
 * State management for permission group CRUD operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import permissionGroupService from '../../services/permissionGroupService';
import alertify from 'alertifyjs';

// ============= ASYNC THUNKS =============

/**
 * Fetch all permission groups for current company
 * @param {string} companyId - Optional: Filter by company ID (for system admin)
 */
export const fetchPermissionGroups = createAsyncThunk(
  'permissionGroup/fetchPermissionGroups',
  async (companyId = null, { rejectWithValue }) => {
    try {
      return await permissionGroupService.getPermissionGroups(companyId);
    } catch (error) {
      const message = error.response?.data?.message || 'Gruplar yüklenemedi';
      return rejectWithValue(message);
    }
  }
);

/**
 * Create new permission group
 */
export const createPermissionGroup = createAsyncThunk(
  'permissionGroup/createPermissionGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      const group = await permissionGroupService.createPermissionGroup(groupData);
      alertify.success('Yetki grubu başarıyla oluşturuldu');
      return group;
    } catch (error) {
      const message = error.response?.data?.message || 'Grup oluşturulamadı';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Update permission group
 */
export const updatePermissionGroup = createAsyncThunk(
  'permissionGroup/updatePermissionGroup',
  async ({ groupId, updates }, { rejectWithValue }) => {
    try {
      const group = await permissionGroupService.updatePermissionGroup(groupId, updates);
      alertify.success('Grup güncellendi. Değişiklikler hemen yansıyacak.');
      return group;
    } catch (error) {
      const message = error.response?.data?.message || 'Grup güncellenemedi';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Delete permission group
 */
export const deletePermissionGroup = createAsyncThunk(
  'permissionGroup/deletePermissionGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      await permissionGroupService.deletePermissionGroup(groupId);
      alertify.success('Grup başarıyla silindi');
      return groupId;
    } catch (error) {
      const message = error.response?.data?.message || 'Grup silinemedi';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Get user's permission groups
 */
export const fetchUserPermissionGroups = createAsyncThunk(
  'permissionGroup/fetchUserPermissionGroups',
  async (userId, { rejectWithValue }) => {
    try {
      return await permissionGroupService.getUserPermissionGroups(userId);
    } catch (error) {
      const message = error.response?.data?.message || 'Kullanıcı grupları yüklenemedi';
      return rejectWithValue(message);
    }
  }
);

/**
 * Assign permission groups to user
 */
export const assignUserPermissionGroups = createAsyncThunk(
  'permissionGroup/assignUserPermissionGroups',
  async ({ userId, groupIds }, { rejectWithValue }) => {
    try {
      const result = await permissionGroupService.assignPermissionGroups(userId, groupIds);
      alertify.success('Kullanıcı yetkileri güncellendi');
      return result;
    } catch (error) {
      const message = error.response?.data?.message || 'Yetkiler atanamadı';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

// ============= SLICE =============

const permissionGroupSlice = createSlice({
  name: 'permissionGroup',
  initialState: {
    groups: [],
    userGroups: null, // { user, assigned, available }
    isLoading: false,
    error: null
  },
  reducers: {
    clearPermissionGroups: (state) => {
      state.groups = [];
      state.userGroups = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== Fetch Permission Groups ==========
      .addCase(fetchPermissionGroups.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPermissionGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        state.groups = action.payload;
      })
      .addCase(fetchPermissionGroups.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // ========== Create Permission Group ==========
      .addCase(createPermissionGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPermissionGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.groups.push(action.payload);
      })
      .addCase(createPermissionGroup.rejected, (state) => {
        state.isLoading = false;
      })
      
      // ========== Update Permission Group ==========
      .addCase(updatePermissionGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePermissionGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.groups.findIndex(g => g._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(updatePermissionGroup.rejected, (state) => {
        state.isLoading = false;
      })
      
      // ========== Delete Permission Group ==========
      .addCase(deletePermissionGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePermissionGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.groups = state.groups.filter(g => g._id !== action.payload);
      })
      .addCase(deletePermissionGroup.rejected, (state) => {
        state.isLoading = false;
      })
      
      // ========== Fetch User Permission Groups ==========
      .addCase(fetchUserPermissionGroups.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissionGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userGroups = action.payload;
      })
      .addCase(fetchUserPermissionGroups.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // ========== Assign User Permission Groups ==========
      .addCase(assignUserPermissionGroups.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(assignUserPermissionGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update userGroups if needed
        if (state.userGroups) {
          state.userGroups = {
            ...state.userGroups,
            ...action.payload
          };
        }
      })
      .addCase(assignUserPermissionGroups.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const { clearPermissionGroups } = permissionGroupSlice.actions;
export default permissionGroupSlice.reducer;

